// D9-05 (#408) — a literal, verbatim transcription of
// `apps/tenant/app/Modules/Service/Support/ServiceEditLock.php`'s three
// const arrays. Kept as literal lists — never computed by subtracting the
// locked-out cases from the enum's own full case list — for the same reason
// the PHP source gives: a literal list is greppable and cannot silently
// change shape when a new enum case is added elsewhere.
//
// Every one of the 24 verbs (see `verbs.ts`) is EXEMPT from these locks — a
// verb is a lifecycle change, not an edit. Never wire an edit-lock check onto
// a verb button; these predicates gate PATCH-style edits only.
import type { InvoiceState, JobPartStatus, JobState, WorkOrderState } from '../types/service.types';

/**
 * Non-terminal and pre-invoice WorkOrder states, plus `Reopened` (D16-40:
 * editable for non-billing fields; billing is closed once invoiced, see
 * `isBillingClosed`).
 */
export const WORK_ORDER_EDITABLE: readonly WorkOrderState[] = [
  'Draft',
  'AwaitingDropoff',
  'InShop',
  'InProcess',
  'AwaitingParts',
  'AwaitingApproval',
  'Paused',
  'Completed',
  'ReadyForCollection',
  'Reopened',
];

/** Every Job state except `Completed` and `Cancelled`. */
export const JOB_EDITABLE: readonly JobState[] = [
  'Pending',
  'Scheduled',
  'InProgress',
  'AwaitingParts',
  'Paused',
  'OnHoldInternal',
  'AwaitingApproval',
  'AwaitingSubletReturn',
  'ReadyForQA',
  'NeedsRework',
];

/** The line's own terminal lock — a part in either status may no longer be PATCHed. */
export const JOB_PART_LOCKED: readonly JobPartStatus[] = ['installed', 'returned'];

export const isWorkOrderEditable = (state: WorkOrderState): boolean =>
  WORK_ORDER_EDITABLE.includes(state);

export const isJobEditable = (state: JobState): boolean => JOB_EDITABLE.includes(state);

export const isJobPartLocked = (status: JobPartStatus): boolean =>
  JOB_PART_LOCKED.includes(status);

/**
 * D16-21 / D16-39 — once a work order has been invoiced, add-line and
 * money-field controls are hidden or disabled with the reverse-and-reissue
 * hint. The API refuses with WORK_ORDER_BILLING_CLOSED (or ACCOUNTING_LOCKED
 * first on a locked row). Keys on `invoice_state`, never the aggregate state.
 */
export const isBillingClosed = (invoiceState: InvoiceState): boolean => invoiceState !== 'draft';
